import {
  BannerImage,
  CardCarousel,
  CardCarouselBanner,
  SimplePaginator,
  VerticalPositionType
} from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  AEMErrorMessage,
  checkErrors,
  getEmptyContent,
  getGenericLabel,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { GenericLabel, logError } from '@com-delta-idp/mnx-utils';
import {
  AEMOverview,
  CardBanner,
  CardList
} from '../models/aem/overview.interface';

export const getCardBanner = (
  content: AEMOverview | undefined,
  featureFlag: boolean | undefined,
  cardType: string,
  errors: (string | undefined)[],
  aemErrorMessages?: AEMErrorMessage[] | undefined
): CardCarousel => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<CardCarousel>(
        'card-banner',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      string,
      [AEMOverview | undefined, string | undefined, boolean | undefined]
    >('cardList', content, cardType, featureFlag)
      ? getCardCarousel(
          cardType,
          content?.cardList as CardList,
          featureFlag as boolean
        )
      : isDisplay<CardCarousel>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'CARD_BANNER');
    return getEmptyContent<CardCarousel>('card-banner', aemErrorMessages, true);
  }
};

const getBannerCardLabels = (
  card: CardBanner,
  subtitle: string
): GenericLabel => getGenericLabel(card.title, subtitle);

const getImage = (
  position: VerticalPositionType,
  alt: string,
  src: string
): BannerImage => ({ position, alt, src, show: true });

const getPaginator = (
  total: number,
  cardIndex: number,
  card: CardList
): SimplePaginator => {
  return {
    total,
    current: cardIndex,
    label: `${card.label} (${cardIndex}/${total})`,
    ariaLabels: {
      indicator: `${card.label} (${cardIndex}/${total})`,
      ...card.ariaLabels
    }
  };
};

const getCardCarousel = (
  cardType: string,
  content: CardList,
  featureFlag: boolean
): CardCarousel => {
  if (isValidCardParams(content, cardType, featureFlag)) {
    const cardTypes: string[] = cardType?.split(':');
    const filteredCards: CardBanner[] = content?.cardBanner?.filter(
      // Checking whether the card type is available in the cards types
      (card: CardBanner): boolean => cardTypes.indexOf(card.type) > -1
    );
    const cards: CardCarouselBanner[] = [] as CardCarouselBanner[];
    // Building the card carousel object from the filtered cards above
    cardTypes.forEach((cardType: string): void => {
      const card: CardBanner | undefined = filteredCards.find(
        (card) => card.type === cardType
      );
      if (card) {
        const labels: GenericLabel = getBannerCardLabels(card, '');
        const image: BannerImage = getImage(
          content.position,
          card.imageAlt,
          card.imageSrc
        );
        cards.push({ labels, image });
      }
    });
    const paginator: SimplePaginator =
      cards.length > 0
        ? getPaginator(cards.length, 1, content)
        : ({} as SimplePaginator);
    return cards.length > 0
      ? {
          cards,
          paginator,
          current: 1,
          disabled: !featureFlag,
          loaded: true
        }
      : getEmptyContent<CardCarousel>('', [], true, true);
  } else {
    return getEmptyContent<CardCarousel>('', [], true, true);
  }
};

const isValidCardParams = (
  content: CardList,
  cardType: string,
  featureFlag: boolean
): boolean => {
  return (
    content &&
    content?.cardBanner?.length > 0 &&
    cardType.trim().length > 0 &&
    featureFlag
  );
};
